import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import TestReport from '../components/TestReport';
import MedicalQuestionnaireProgressBanner from '../components/MedicalQuestionnaireProgressBanner';
import bloodService from '../services/blood';
import { setReports } from '../store/bloodTestSlice';
import { compareBloodTestsByResultThenTestDate } from '../utils/bloodTestHeaderDates';
import { PORTAL_PAGE_CONTAINER_CLASS } from '../utils/constants';

export default function BloodTestSummaryPage() {
  const dispatch = useDispatch();
  const reports = useSelector((state) => state.bloodTest.reports || []);
  const userRole = useSelector((state) => state.user.role || '');
  const isSupervisor = userRole === 'Supervisor';
  const [expandedIds, setExpandedIds] = React.useState({});
  const [loading, setLoading] = React.useState(false);

  const makeId = (r) =>
    `${r.header?.type || ''}|${r.header?.testDate || ''}|${r.header?.resultDate || ''}`;

  const sortedReports = React.useMemo(() => {
    const clone = [...reports];
    clone.sort(compareBloodTestsByResultThenTestDate);
    return clone;
  }, [reports]);

  const allAirtableReports = React.useMemo(() => {
    const fromApi = (sortedReports || []).map((r) => ({
      customerId: String(r?.customerId || '').trim(),
      recordId: String(r?.recordId || '').trim(),
      airtablePreviewUrl: String(r?.airtablePreviewUrl || '').trim(),
    }));
    return fromApi;
  }, [sortedReports]);

  const toggle = (id) => setExpandedIds((m) => ({ ...m, [id]: !m[id] }));

  React.useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        setLoading(true);
        dispatch(setReports([]));
        setExpandedIds({});
        const mapped = await bloodService.fetchBloodSummary();
        if (cancelled) return;
        dispatch(setReports(Array.isArray(mapped) ? mapped : []));
      } catch (e) {
        if (!cancelled) dispatch(setReports([]));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [dispatch]);

  React.useEffect(() => {
    if (
      !loading &&
      sortedReports.length > 0 &&
      Object.keys(expandedIds).length === 0
    ) {
      const firstId = makeId(sortedReports[0]);
      setExpandedIds({ [firstId]: true });
    }
  }, [sortedReports, loading]);

  return (
    <div className="min-w-screen min-h-screen bg-[#F9F5F2] py-8 md:py-12">
      <div className={PORTAL_PAGE_CONTAINER_CLASS}>
        <MedicalQuestionnaireProgressBanner />
        <div className="-mt-2 mb-6 md:mb-12">
          <div className="flex flex-col gap-4 lg:grid lg:grid-cols-[minmax(0,1fr)_auto_minmax(max-content,1fr)] lg:items-center lg:gap-x-3">
            <div className="hidden min-w-0 lg:block" aria-hidden="true" />
            <h1 className="min-w-0 text-center text-2xl md:text-4xl font-libreBaskerville text-[#2B3133]">
              Blood Test Summary
            </h1>
            <div className="flex shrink-0 flex-nowrap items-center justify-center gap-3 lg:justify-self-end">
              {isSupervisor && (
                <Link
                  to="/medical-questionnaire-summary"
                  className="inline-flex h-[40px] items-center justify-center rounded-full bg-[#119975] px-5 font-poppins text-sm font-medium text-white transition-opacity hover:opacity-95 md:h-[48px] md:px-6 md:text-base"
                >
                  View Summary
                </Link>
              )}
              <Link
                to="/register-new-test"
                className="inline-flex items-center gap-2 md:gap-3 bg-[#119975] text-[#FFFFFF] pl-2 md:pl-3 pr-4 md:pr-6 h-[40px] md:h-[48px] rounded-full font-poppins text-sm md:text-base hover:opacity-95 transition-opacity"
              >
                <span className="w-7 h-7 md:w-8 md:h-8 grid place-items-center text-[18px] md:text-[20px] leading-none text-[#FFFFFF]">
                  +
                </span>
                <span>Register New Test</span>
              </Link>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin h-8 w-8 border-4 border-[#C1DCD4] border-t-[#119975] rounded-full" />
          </div>
        ) : sortedReports.length === 0 ? (
          <div className="flex items-center justify-center px-6 min-h-[60vh]">
            <div className="text-center max-w-md">
              <h2 className="mb-4 text-3xl md:text-4xl font-libreBaskerville font-semibold text-[#119975]">
                No reports to show
              </h2>
              <p className="text-[#889696] font-poppins text-sm md:text-lg">
                We couldn&apos;t find any blood test reports for this account
                yet. Please check back later.
              </p>
            </div>
          </div>
        ) : (
          <div className="space-y-6 md:space-y-8">
            {sortedReports.map((report) => {
              const reportId = makeId(report);
              return (
                <div
                  key={reportId}
                  className="bg-white rounded-2xl p-4 md:p-8 shadow-sm"
                >
                  <TestReport
                    report={report}
                    expanded={!!expandedIds[reportId]}
                    onToggle={() => toggle(reportId)}
                    allAirtableReports={allAirtableReports}
                  />
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
